import { ReactNode } from 'react'
import { cn } from '../../lib/utils'

type FontSize = '2xs' | 'xs' | 'sm' | 'base' | 'md' | 'lg' | 'xl' | '2xl'
type FontFamily = 'inter' | 'fragment'
type Variant = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'p' | 'span' | 'div'

export interface TypographyProps {
  weight?: 'regular' | 'medium' | 'semiBold'
  italic?: boolean
  children: ReactNode
  className?: string
  size?: FontSize
  font?: FontFamily
  variant?: Variant
  muted?: boolean
}

const sizeStyles = {
  '2xs': 'text-[8px]',
  xs: 'text-[12px]',
  sm: 'text-[14px]',
  base: 'text-[16px]',
  md: 'text-[20px]',
  lg: 'text-[28px]',
  xl: 'text-[40px]',
  '2xl': 'text-[64px]',
}

const fontStyles = {
  inter: 'font-inter',
  fragment: 'font-fragment-mono',
}

const weightStyles = {
  regular: 'font-normal',
  medium: 'font-medium',
  semiBold: 'font-semibold',
  bold: 'font-bold',
}

export function Typography({
  weight = 'regular',
  italic = false,
  children,
  className,
  size = 'base',
  font = 'inter',
  variant = 'p',
  muted = false,
}: TypographyProps) {
  const Component = variant
  const isFragment = font === 'fragment'
  const fontWeightClass = isFragment ? weightStyles['regular'] : weightStyles[weight]
  return (
    <Component
      className={cn(
        sizeStyles[size],
        fontStyles[font],
        fontWeightClass,
        isFragment && italic && 'italic',
        muted && 'text-muted-foreground',
        variant.startsWith('h') && 'tracking-tight',
        className
      )}
    >
      {children}
    </Component>
  )
}
